import React, { useState, useEffect } from 'react';
import { 
  ArrowLeft, 
  Award, 
  ChevronRight,
  Medal,
  Eye,
  Download,
  X,
  Loader,
  Info,
  ShieldCheck,
  Calendar,
  FileText
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { senvraApi } from '../utils/axiosInstance';
import logo from "../pages/certificateAssests/logo.png";
import msme from "../pages/certificateAssests/msme.png";
import dpit from "../pages/certificateAssests/dpiit.png";
import sign from "../pages/certificateAssests/sign.png";
import badge from "../pages/certificateAssests/badge.png";

// ==================== IMAGE TO BASE64 ====================
const getBase64Image = async (imagePath) => {
  try {
    const response = await fetch(imagePath);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Error converting image to base64:', error);
    return imagePath;
  }
};

// ==================== CERTIFICATE HTML TEMPLATE ====================
const generateCertificateHtml = (data) => {
  return `
<style>
    .certificate {
        width: 1122px;
        height: 793px;
        position: relative; overflow: hidden;
        border: 18px solid #0b2a4a;
        box-sizing: border-box;
        background: radial-gradient(circle, #ffffff 0%, #fcf9f2 100%);
        margin: auto;
        flex-shrink: 0;
        font-family: "Times New Roman", serif;
        color: #333;
    }
    .gold-frame {
        position: absolute; top: 8px; left: 8px; right: 8px; bottom: 8px;
        border: 2px solid #d4af37;
        pointer-events: none;
    }
    .logo-left { position: absolute; top: 50px; left: 55px; }
    .logo-left img { width: 210px; }
    .badge-frame {
        position: absolute;
        top: 9px;
        right: 70px;
    }
    .badge-frame img {
        width: 140px;
        filter: drop-shadow(0px 5px 10px rgba(0,0,0,0.15));
    }
    .meta-info {
        margin: 20px auto 0 auto;
        width: 100%;
        text-align: center;
        font-size: 15px;
        color: #777;
        letter-spacing: 1px;
    }
    .main-content { text-align: center; margin-top: 100px; padding: 0 100px; }
    h1 {
        font-size: 55px;
        color: #0b2a4a; margin: 0;
        letter-spacing: 10px;
        font-weight: bold;
        text-transform: uppercase;
    }
    h2 {
        font-size: 22px; letter-spacing: 6px; color: #d4af37;
        margin: 10px 0 45px 0; font-weight: normal;
    }
    .presented-text { font-size: 22px; margin-bottom: 15px; font-style: italic; color: #555; }
    .name {
        font-size: 49px; font-weight: bold; color: #0b2a4a;
        display: inline-block; padding: 0 40px;
    }
    .name-line {
        width: 50%; border-top: 1px solid #cfcfcf;
        margin: 12px auto 25px auto;
    }
    .desc {
        font-size: 19px; line-height: 1.6; width: 80%; margin: 0 auto; color: #444;
    }
    .footer-table {
        position: absolute; bottom: 55px; width: 100%;
        border-collapse: collapse; table-layout: fixed;
    }
    .footer-col { text-align: center; vertical-align: bottom; padding: 0 20px; }
    .footer-sign { height: 80px; margin-bottom: 12px; }
    .footer-center-logo { height: 95px; margin-bottom: 10px; }
    .footer-msme { width: 180px; height: auto; margin-bottom: 10px; }
    .sig-line {
        width: 220px; margin: 0 auto;
        border-top: 2px solid #0b2a4a;
        padding-top: 10px; font-size: 15px; font-weight: bold; color: #0b2a4a;
    }
    .address-bar {
        position: absolute; bottom: 0; width: 100%;
        text-align: center; font-size: 13px; color: #fff;
        background-color: #0b2a4a; padding: 10px 0; letter-spacing: 1px;
    }
</style>
<div class="certificate">
    <div class="gold-frame"></div>
    <div class="logo-left">
        <img src="${data.logo}" alt="Logo" />
    </div>
    <div class="meta-info">
        Certificate ID: <strong>${data.certificate_id}</strong> &nbsp; | &nbsp; Date: <strong>${data.issued_date}</strong>
    </div>
    <div class="badge-frame">
        <img src="${data.badge}" alt="Medal" />
    </div>
    <div class="main-content">
        <h1>CERTIFICATE</h1>
        <h2>OF PROFESSIONAL ACHIEVEMENT</h2>
        <div class="presented-text">This Certificate is Proudly Presented To</div>
        <div class="name">${data.name}</div>
        <div class="name-line"></div>
        <div class="desc">
            Has successfully completed the
            <span style="font-weight: bold; font-size: 22px; color: #0b2a4a;">${data.course_name}</span>
            training program on <span style="font-weight: bold; font-size: 22px; color: #0b2a4a;">${data.start_date}</span>
            and fulfilled all academic and practical requirements.
            The candidate has demonstrated technical proficiency, analytical skills,
            and professional competence in accordance with organizational standards.
        </div>
    </div>
    <table class="footer-table">
        <tr>
            <td class="footer-col">
                <img src="${data.sign}" class="footer-sign" />
                <div class="sig-line">Managing Director</div>
            </td>
            <td class="footer-col">
                <img src="${data.dpit}" class="footer-center-logo" />
            </td>
            <td class="footer-col">
                <img src="${data.msme}" class="footer-msme" />
                <div class="sig-line">Ministry of MSME</div>
            </td>
        </tr>
    </table>
    <div class="address-bar">
        SENVRA AI &#8226; Hyderabad &#8226; www.senvraai.com
    </div>
</div>
  `;
};

const generateFullHtmlPage = (data) => {
  const certHtml = generateCertificateHtml(data);
  return `<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8">
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  html, body {
    width: 1122px;
    height: 793px;
    overflow: hidden;
    background: white;
  }
</style>
</head>
<body>
${certHtml}
</body>
</html>`;
};

// ==================== PDF DOWNLOAD FUNCTION ====================
const downloadCertificateAsPDF = async (certHtmlData) => {
  try {
    const fullHtml = generateFullHtmlPage(certHtmlData);

    // Create hidden iframe
    const iframe = document.createElement('iframe');
    iframe.style.cssText = `
      position: fixed;
      top: -9999px;
      left: -9999px;
      width: 1122px;
      height: 793px;
      border: none;
      visibility: hidden;
    `;
    document.body.appendChild(iframe);
    iframe.contentDocument.open();
    iframe.contentDocument.write(fullHtml);
    iframe.contentDocument.close();

    // Wait for iframe to load
    await new Promise((resolve) => {
      iframe.onload = resolve;
      setTimeout(resolve, 2000);
    });

    const html2canvas = (await import('html2canvas')).default;
    const { jsPDF } = await import('jspdf');

    const certElement = iframe.contentDocument.querySelector('.certificate');

    const canvas = await html2canvas(certElement, {
      scale: 3,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      width: 1122,
      height: 793,
      windowWidth: 1122,
      windowHeight: 793,
    });

    const pdf = new jsPDF({
      orientation: 'landscape',
      unit: 'px',
      format: [1122, 793],
    });

    pdf.addImage(
      canvas.toDataURL('image/jpeg', 1.0),
      'JPEG',
      0, 0,
      1122, 793
    );

    pdf.save(`Senvra_Certificate_${certHtmlData.certificate_id || 'Download'}.pdf`);
    document.body.removeChild(iframe);

    return true;
  } catch (error) {
    console.error('PDF download failed:', error);
    throw error;
  }
};

// ==================== MAIN COMPONENT ====================
const Mycertificates = () => {
  const navigate = useNavigate();

  const [certificates, setCertificates] = useState([]);
  const [totalCertificates, setTotalCertificates] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [base64Images, setBase64Images] = useState({
    logo: null, badge: null, sign: null, dpit: null, msme: null,
  });

  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCert, setSelectedCert] = useState(null);
  const [certificateUrl, setCertificateUrl] = useState('');
  const [isPdfLoading, setIsPdfLoading] = useState(false);

  // Load base64 images
  useEffect(() => {
    const loadImages = async () => {
      try {
        const [logoB64, badgeB64, signB64, dpitB64, msmeB64] = await Promise.all([
          getBase64Image(logo), getBase64Image(badge), getBase64Image(sign),
          getBase64Image(dpit), getBase64Image(msme),
        ]);
        setBase64Images({
          logo: logoB64, badge: badgeB64, sign: signB64,
          dpit: dpitB64, msme: msmeB64,
        });
      } catch (error) {
        console.error('Error loading images:', error);
      }
    };
    loadImages();
  }, []);

  // Fetch certificates
  useEffect(() => {
    const fetchCertificates = async () => {
      setIsLoading(true);
      setError('');
      try {
        const response = await senvraApi.get('/certificate/myCertificates');
        if (response.data?.success) {
          setCertificates(response.data.certificates || []);
          setTotalCertificates(response.data.total_certificates || 0);
        } else {
          throw new Error('Invalid response format');
        }
      } catch (err) {
        console.error('Error fetching certificates:', err);
        setError(err.response?.data?.message || 'Failed to load certificates');
      } finally {
        setIsLoading(false);
      }
    };
    fetchCertificates();
  }, []);

  // Get current user name from localStorage
  const getDisplayName = () => {
    try {
      const user = localStorage.getItem('user');
      if (user) {
        const parsed = JSON.parse(user);
        return parsed.name || parsed.fullName || parsed.email?.split('@')[0] || 'User';
      }
      const userData = localStorage.getItem('user_data');
      if (userData) {
        const parsed = JSON.parse(userData);
        return parsed.fullName || parsed.name || 'User';
      }
    } catch (e) {
      // fallback
    }
    return 'User';
  };

  // View certificate (open modal)
  const handleViewCertificate = (cert) => {
    setSelectedCert(cert);
    const certHtmlData = {
      name: getDisplayName(),
      course_name: cert.course_name || 'Skill Course',
      certificate_id: cert.cert_id,
      issued_date: cert.issue_date || new Date().toLocaleDateString(),
      start_date: cert.issue_date || new Date().toLocaleDateString(),
      logo: base64Images.logo,
      badge: base64Images.badge,
      sign: base64Images.sign,
      dpit: base64Images.dpit,
      msme: base64Images.msme,
    };
    const fullHtml = generateFullHtmlPage(certHtmlData);
    const blob = new Blob([fullHtml], { type: 'text/html' });
    setCertificateUrl(URL.createObjectURL(blob));
    setIsModalOpen(true);
  };

  // Download PDF
  const handleDownloadPdf = async (cert) => {
    setIsPdfLoading(true);
    try {
      const certHtmlData = {
        name: getDisplayName(),
        course_name: cert?.course_name || 'Skill Course',
        certificate_id: cert?.cert_id || 'CERT-UNKNOWN',
        issued_date: cert?.issue_date || new Date().toLocaleDateString(),
        start_date: cert?.issue_date || new Date().toLocaleDateString(),
        logo: base64Images.logo,
        badge: base64Images.badge,
        sign: base64Images.sign,
        dpit: base64Images.dpit,
        msme: base64Images.msme,
      };
      await downloadCertificateAsPDF(certHtmlData);
    } catch (err) {
      console.error('PDF generation failed:', err);
      alert('Failed to download PDF. Please try again.');
    } finally {
      setIsPdfLoading(false);
    }
  };

  // Type badge color
  const getTypeBadge = (type) => {
    if (type === 'quiz') {
      return { bg: 'bg-violet-50', text: 'text-violet-600', label: 'Assessment' };
    } else if (type === 'internship') {
      return { bg: 'bg-amber-50', text: 'text-amber-600', label: 'Internship' };
    }
    return { bg: 'bg-slate-50', text: 'text-slate-600', label: type || 'General' };
  };

  return (
    <div style={{ marginTop: "72px" }} className="min-h-screen bg-[#f8fafc] p-8 md:p-12 font-sans">
      {/* Top Navigation */}
      <div className="max-w-7xl mx-auto mb-8">
        <button
          onClick={() => navigate("/dashboards")}
          className="flex items-center gap-2 text-[#64748b] hover:text-blue-500 transition-colors font-semibold text-sm"
        >
          <ArrowLeft size={18} />
          Back to Dashboard
        </button>
      </div>

      {/* Header Section */}
      <header className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-16">
        <div className="space-y-2">
          <h1 className="text-5xl font-serif text-[#1e293b]">My Certificates</h1>
          <p className="text-[#64748b] text-lg">
            View and manage all your verified professional certifications in one place.
          </p>
        </div>

        {/* Certificates Earned Metric Card */}
        <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm flex items-center gap-12 min-w-[280px]">
          <div className="space-y-1">
            <p className="text-[10px] font-bold text-slate-400 tracking-widest uppercase">
              CERTIFICATES EARNED
            </p>
            <p className="text-5xl font-bold text-[#1e293b]">
              {isLoading ? '...' : totalCertificates}
            </p>
          </div>
          <div className="bg-white border-2 border-blue-100 p-3 rounded-full">
            <Award className="text-blue-400" size={32} strokeWidth={1.5} />
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="max-w-7xl mx-auto">
        {isLoading ? (
          // Loading state
          <div className="w-full max-w-4xl mx-auto bg-white border border-slate-200 rounded-[2.5rem] p-16 flex flex-col items-center text-center">
            <Loader size={40} className="animate-spin text-blue-500 mb-4" />
            <p className="text-slate-500 text-lg">Loading your certificates...</p>
          </div>
        ) : error ? (
          // Error state
          <div className="w-full max-w-4xl mx-auto bg-white border border-slate-200 rounded-[2.5rem] p-16 flex flex-col items-center text-center">
            <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mb-4">
              <X size={30} className="text-red-400" />
            </div>
            <p className="text-red-500 text-lg mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="text-blue-500 font-semibold hover:underline"
            >
              Try Again
            </button>
          </div>
        ) : certificates.length === 0 ? (
          // Empty state
          <div className="w-full max-w-4xl mx-auto bg-white border-2 border-dashed border-slate-200 rounded-[2.5rem] p-16 flex flex-col items-center text-center">
            <div className="w-16 h-16 bg-slate-50 border border-slate-100 rounded-full flex items-center justify-center mb-8">
              <Medal className="text-slate-300" size={30} strokeWidth={1.5} />
            </div>
            <h2 className="text-3xl font-serif text-[#1e293b] mb-4">No Certificates Yet</h2>
            <p className="text-slate-500 text-lg max-w-md leading-relaxed mb-10">
              Once you pass your assessment tests and complete the verification process,
              your certificates will appear here.
            </p>
            <button
              onClick={() => navigate('/browse-courses')}
              className="bg-[#0ea5e9] hover:bg-blue-600 text-white font-bold py-4 px-10 rounded-2xl flex items-center gap-3 transition-all transform hover:scale-105 shadow-lg shadow-blue-500/20"
            >
              Start Assessing
              <ChevronRight size={20} />
            </button>
          </div>
        ) : (
          // Certificate cards grid
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {certificates.map((cert) => {
              const typeBadge = getTypeBadge(cert.type);
              return (
                <div
                  key={cert.cert_id}
                  className="bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-lg transition-all duration-300 p-6 flex flex-col"
                >
                  {/* Top row: icon + type badge */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center">
                        <ShieldCheck size={20} className="text-white" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-400 font-semibold uppercase tracking-wider">Verified</p>
                      </div>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${typeBadge.bg} ${typeBadge.text}`}>
                      {typeBadge.label}
                    </span>
                  </div>

                  {/* Course name */}
                  <h3 className="text-xl font-bold text-[#1e293b] mb-3 leading-tight">
                    {cert.course_name}
                  </h3>

                  {/* Meta info */}
                  <div className="flex items-center gap-4 text-sm text-slate-500 mb-6">
                    <div className="flex items-center gap-1">
                      <Calendar size={14} />
                      <span>{cert.issue_date}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <FileText size={14} />
                      <span className="font-mono text-xs">{cert.cert_id}</span>
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div className="mt-auto flex gap-3">
                    <button
                      onClick={() => handleViewCertificate(cert)}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl transition-all font-semibold text-sm"
                    >
                      <Eye size={16} />
                      View
                    </button>
                    <button
                      onClick={() => handleDownloadPdf(cert)}
                      disabled={isPdfLoading}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white rounded-xl transition-all font-semibold text-sm shadow-md disabled:opacity-50"
                    >
                      {isPdfLoading ? (
                        <Loader size={16} className="animate-spin" />
                      ) : (
                        <Download size={16} />
                      )}
                      PDF
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* ==================== CERTIFICATE PREVIEW MODAL ==================== */}
      {isModalOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/60 backdrop-blur-md p-4 md:p-8">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden transform transition-all flex flex-col border border-white/30">
            {/* Modal Header */}
            <div className="flex justify-between items-center px-6 py-4 bg-gradient-to-r from-slate-50 to-white border-b border-slate-100 flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="bg-gradient-to-r from-emerald-500 to-green-500 p-2 rounded-xl shadow-md">
                  <Award size={20} className="text-white" />
                </div>
                <div>
                  <h4 className="text-lg font-bold text-slate-800">Your Certificate of Achievement</h4>
                  <p className="text-xs text-slate-500">{selectedCert?.course_name}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => handleDownloadPdf(selectedCert)}
                  disabled={isPdfLoading}
                  className="hidden sm:flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white rounded-xl transition-all shadow-md active:scale-95 disabled:opacity-50 font-semibold text-sm"
                >
                  {isPdfLoading ? <Loader size={18} className="animate-spin" /> : <Download size={18} />}
                  Download PDF
                </button>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="p-2 rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6 bg-gradient-to-br from-slate-100/50 to-white flex flex-col items-center">
              {certificateUrl ? (
                <div className="w-full flex justify-center">
                  <div className="bg-white p-3 rounded-xl shadow-2xl border border-slate-200">
                    <div
                      className="overflow-hidden rounded-lg relative"
                      style={{
                        width: 'min(750px, 85vw)',
                        height: 'calc(min(750px, 85vw) * 0.706)',
                        maxWidth: '100%',
                      }}
                    >
                      <iframe
                        src={certificateUrl}
                        title="Certificate"
                        scrolling="no"
                        className="border-0 absolute top-0 left-0"
                        style={{
                          width: '1122px',
                          height: '793px',
                          transform: `scale(${Math.min(750, window.innerWidth * 0.85) / 1122})`,
                          transformOrigin: 'top left',
                        }}
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-20">
                  <Loader size={32} className="animate-spin text-blue-500" />
                  <p className="text-slate-500 mt-4">Loading certificate preview...</p>
                </div>
              )}

              {/* Mobile download button */}
              {certificateUrl && (
                <button
                  onClick={() => handleDownloadPdf(selectedCert)}
                  disabled={isPdfLoading}
                  className="mt-6 flex sm:hidden items-center gap-2 px-6 py-3 bg-gradient-to-r from-emerald-500 to-green-600 text-white rounded-xl font-bold w-full justify-center shadow-md"
                >
                  <Download size={18} /> Download PDF Certificate
                </button>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 bg-white border-t border-slate-100 flex flex-col sm:flex-row gap-4 justify-between items-center flex-shrink-0">
              <div className="flex items-center gap-2 text-slate-500 bg-slate-50 px-3 py-1.5 rounded-full">
                <Info size={14} className="text-blue-500 flex-shrink-0" />
                <p className="text-[11px] leading-tight">
                  This certificate is a verified record of your achievement. Download anytime.
                </p>
              </div>
              <button
                onClick={() => setIsModalOpen(false)}
                className="px-6 py-2 bg-slate-100 text-slate-700 font-semibold rounded-xl hover:bg-slate-200 transition-colors text-sm"
              >
                Close Preview
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Mycertificates;